import React, { useState } from "react";
import { Icon } from "@iconify/react";
import styled from "styled-components";

const OrderChart = ({ orders, producturl }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [sortField, setSortField] = useState("quantity");
  const [sortDirection, setSortDirection] = useState("desc");

  if (!orders || !orders.data) {
    return (
      <EmptyState>
        <Icon icon="solar:box-minimalistic-bold" className="icon" />
        <p>No orders data loaded to compile analytics.</p>
      </EmptyState>
    );
  }

  // 1. Group and aggregate identical products
  const productSummaryMap = {};
  orders.data.forEach((order) => {
    const name = order.product_name || "Unknown Product";
    const qty = parseInt(order.quantity) || 0;
    const price = parseFloat(order.sale_price || order.mrp || 0);
    const thumbnail = order.thumbnail || "";

    if (productSummaryMap[name]) {
      productSummaryMap[name].quantity += qty;
      productSummaryMap[name].totalRevenue += qty * price;
    } else {
      productSummaryMap[name] = {
        name,
        thumbnail,
        price,
        quantity: qty,
        totalRevenue: qty * price
      };
    }
  });

  const rawSummaryList = Object.values(productSummaryMap);

  // 2. Filter list by search query
  const filteredList = rawSummaryList.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 3. Sort list dynamically
  const sortedList = [...filteredList].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (typeof valA === "string") {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // 4. Paginate list local values
  const totalItems = sortedList.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedList.slice(indexOfFirstItem, indexOfLastItem);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatPrice = (amount) => {
    return `₹${Math.round(amount).toLocaleString()}/-`;
  };

  return (
    <AnalyticsCard>
      {/* Table Header Section */}
      <TableHeaderRow>
        <div className="title-block">
          <Icon icon="solar:checklist-minimalistic-bold" className="header-icon" />
          <div className="title-texts">
            <h4>Product Ordering Analytics</h4>
            <p>Aggregated statistics of units sold and gross revenue per product</p>
          </div>
        </div>

        {/* Search Input Widget */}
        <SearchBox>
          <Icon icon="solar:magnifer-bold" className="search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </SearchBox>
      </TableHeaderRow>

      {/* Main Data Table */}
      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th onClick={() => handleSort("name")} className="sortable">
                Product Details
                {sortField === "name" && (
                  <Icon icon={sortDirection === "asc" ? "solar:arrow-up-bold" : "solar:arrow-down-bold"} />
                )}
              </th>
              <th onClick={() => handleSort("price")} className="sortable">
                Unit Price
                {sortField === "price" && (
                  <Icon icon={sortDirection === "asc" ? "solar:arrow-up-bold" : "solar:arrow-down-bold"} />
                )}
              </th>
              <th onClick={() => handleSort("quantity")} className="sortable">
                Total Units Sold
                {sortField === "quantity" && (
                  <Icon icon={sortDirection === "asc" ? "solar:arrow-up-bold" : "solar:arrow-down-bold"} />
                )}
              </th>
              <th onClick={() => handleSort("totalRevenue")} className="sortable">
                Gross Revenue
                {sortField === "totalRevenue" && (
                  <Icon icon={sortDirection === "asc" ? "solar:arrow-up-bold" : "solar:arrow-down-bold"} />
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <ProductBlock>
                      <ImageWrapper>
                        <img
                          src={
                            item.thumbnail
                              ? (producturl || "https://keralaspecial.in/server/public/upload/products/") + item.thumbnail
                              : "placeholder.png"
                          }
                          alt={item.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://placehold.co/48x48/f2ece1/123524?text=KS";
                          }}
                        />
                      </ImageWrapper>
                      <span className="product-name" title={item.name}>
                        {item.name}
                      </span>
                    </ProductBlock>
                  </td>
                  <td>
                    <span className="price-tag">{formatPrice(item.price)}</span>
                  </td>
                  <td>
                    <QuantityBadge>{item.quantity} Units</QuantityBadge>
                  </td>
                  <td>
                    <span className="revenue-tag">{formatPrice(item.totalRevenue)}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="empty-row">
                  No products matched your search.
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <PaginationFooter>
          <span className="info-text">
            Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, totalItems)} of {totalItems} items
          </span>
          <ButtonGroup>
            <PageBtn onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <Icon icon="solar:alt-arrow-left-bold" />
            </PageBtn>
            
            {Array.from({ length: totalPages }).map((_, pageIdx) => {
              const pageNum = pageIdx + 1;
              // Only render standard items near the current page
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                Math.abs(pageNum - currentPage) <= 1
              ) {
                return (
                  <PageBtn
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={currentPage === pageNum ? "active" : ""}
                  >
                    {pageNum}
                  </PageBtn>
                );
              }
              if (
                pageNum === 2 ||
                pageNum === totalPages - 1
              ) {
                return <span key={pageNum} className="dots">...</span>;
              }
              return null;
            })}

            <PageBtn onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <Icon icon="solar:alt-arrow-right-bold" />
            </PageBtn>
          </ButtonGroup>
        </PaginationFooter>
      )}
    </AnalyticsCard>
  );
};

export default OrderChart;

// Styled Components
const AnalyticsCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(197, 168, 128, 0.15);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(45, 31, 24, 0.02);
  margin-top: 30px;
  text-align: left;
`;

const TableHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  .title-block {
    display: flex;
    align-items: center;
    gap: 12px;

    .header-icon {
      font-size: 26px;
      color: #123524;
    }

    .title-texts {
      h4 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
        color: #2D1F18;
      }
      p {
        margin: 2px 0 0;
        font-size: 13px;
        color: #666;
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SearchBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    color: #c5a880;
  }

  input {
    width: 100%;
    padding: 10px 14px 10px 42px;
    font-family: "Jost", sans-serif;
    font-size: 13px;
    border: 1px solid rgba(197, 168, 128, 0.25);
    border-radius: 50px;
    background: #FFFFFF;
    color: #2D1F18;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #123524;
      box-shadow: 0 4px 15px rgba(18, 53, 36, 0.08);
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid rgba(197, 168, 128, 0.12);
  border-radius: 12px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 14px;

  thead {
    background: #F2ECE1;
    border-bottom: 1px solid rgba(197, 168, 128, 0.15);

    th {
      padding: 16px 20px;
      font-weight: 600;
      color: #123524;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      white-space: nowrap;

      &.sortable {
        cursor: pointer;
        user-select: none;
        transition: color 0.2s ease;

        svg {
          margin-left: 6px;
          font-size: 12px;
          vertical-align: middle;
        }

        &:hover {
          color: #C5A880;
        }
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid rgba(197, 168, 128, 0.08);
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(242, 236, 225, 0.15);
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 14px 20px;
      vertical-align: middle;
      color: #2D1F18;
    }
  }

  .empty-row {
    text-align: center;
    padding: 30px;
    color: #666;
    font-style: italic;
  }
`;

const ProductBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 380px;

  .product-name {
    font-weight: 600;
    color: #123524;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ImageWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: #FDFBF7;
  border: 1px solid rgba(197, 168, 128, 0.12);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const QuantityBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(197, 168, 128, 0.1);
  color: #8c7623;
  border: 1px solid rgba(197, 168, 128, 0.15);
`;

const PriceTag = styled.span`
  font-weight: 500;
  color: #555;
`;

const RevenueTag = styled.span`
  font-weight: 700;
  color: #123524;
`;

const PaginationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 16px;

  .info-text {
    font-size: 13px;
    color: #666;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  .dots {
    color: #8c9c92;
    padding: 0 4px;
  }
`;

const PageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-family: "Jost", sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: #FFFFFF;
  border: 1px solid rgba(197, 168, 128, 0.2);
  color: #2D1F18;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background: #F2ECE1;
    border-color: #C5A880;
    color: #123524;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.active {
    background: #123524;
    border-color: #123524;
    color: #FFFFFF;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #FFFFFF;
  border: 1px solid rgba(197, 168, 128, 0.15);
  border-radius: 16px;
  margin-top: 30px;

  .icon {
    font-size: 48px;
    color: #c5a880;
    margin-bottom: 12px;
  }

  p {
    font-size: 15px;
    color: #666;
    margin: 0;
    font-style: italic;
  }
`;
